import { sectionKey } from '@/lib/sectionKey';
// ----- Wave E.2 — ClassificationTab ----- //
//
// Shows the classifier's suggestion + confidence and the approval
// workflow. Two paths:
//   1. "Approve this classification" — when the user accepts the
//      classifier's suggestion. No reason needed.
//   2. Override — pick a different section + REQUIRE a reason (Part 11).
//      Reason is enforced client-side (button disabled) AND server-side
//      (HTTP 400 ReasonRequired) — server is the trust boundary.
//
// After approval, shows a small "Approved by ... on ... to ...
// (matched suggestion / OVERRIDE)" history line.
//
// Phase phase-review-surface-strengthen: upgraded to show a clear
// HIGH / MEDIUM / LOW confidence band and a visual distinction between
// agent-suggested (pending review) vs human-confirmed states.

import { useState, type ReactElement, type CSSProperties } from 'react';
import { v2, type Document } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { bandForPercent, confidencePercent } from '@/lib/confidence';
import type { SectionOption } from './PlacementTab';

// phase-classification-stage-1-filename-layer — the Layer 1 hint. Advisory
// only, computed server-side from filename evidence alone; this tab never
// applies it — it's shown so a reviewer has the pattern's track record
// while deciding, exactly the "surfaced as a suggestion, never auto-applied"
// allowance in docs/classification-learning-design-study.md Stage 1.
export interface Layer1ProposalHint {
  readonly status: 'proposal' | 'ambiguous' | null;
  readonly family: string | null;
  readonly section: string | null;
  readonly supportCount: number | null;
}

export interface ClassificationTabProps {
  readonly documentId: string;
  readonly rowVersion?: Document['RowVersion'];
  readonly classifierSuggestion: string | null;
  readonly classifierConfidence: number | null;
  readonly currentApprovedValue: string | null;
  readonly currentApprovedAt: string | null;
  readonly currentApprovedByEmail: string | null;
  readonly sections: readonly SectionOption[];
  readonly canApprove: boolean;
  readonly onSuccess: (approvedClassification: string) => void;
  readonly layer1Proposal?: Layer1ProposalHint | null;
}

/** Display-only humanisation of a family code ("drug-substance" → "Drug
 *  substance") — deliberately NOT a duplicated copy of the backend's family
 *  name list, so there is nothing here that can drift out of sync with it. */
function humanizeFamilyCode(code: string): string {
  const spaced = code.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This document no longer exists. The list may be out of date.',
  ValidationError: 'Provide a valid classification before approving.',
  ReasonRequired: 'A reason is required when overriding the classifier suggestion.',
  PermissionDenied: 'You do not have permission to approve classifications.',
};

// ── Confidence band helpers ────────────────────────────────────────────────────

interface ConfidenceBand {
  label: string;
  bg: string;
  color: string;
  border: string;
  icon: string;
  description: string;
}

// phase-compiler-screen-batch item 2 — the PRESENTATION of a band stays local
// (colours/label/icon are this panel's business), but WHICH band a percentage
// falls in now comes from `lib/confidence`, so this panel and the CTD tree can
// never disagree again. The boundaries are unchanged (70 / 40).
function confidenceBand(pct: number): ConfidenceBand {
  const band = bandForPercent(pct);
  if (band === 'high') {
    return {
      label: 'HIGH CONFIDENCE',
      bg: 'rgba(34,197,94,0.12)',
      color: '#15803D',
      border: 'rgba(34,197,94,0.40)',
      icon: '●',
      description: 'The AI is confident in this placement. Still verify the document matches the section.',
    };
  }
  if (band === 'medium') {
    return {
      label: 'MEDIUM CONFIDENCE',
      bg: 'rgba(245,158,11,0.12)',
      color: '#92400E',
      border: 'rgba(245,158,11,0.50)',
      icon: '◐',
      description: 'The AI has moderate confidence. Review carefully — this placement is more likely to be wrong.',
    };
  }
  return {
    label: 'LOW CONFIDENCE',
    bg: 'rgba(239,68,68,0.12)',
    color: '#B91C1C',
    border: 'rgba(239,68,68,0.50)',
    icon: '○',
    description: 'The AI is uncertain about this placement. Human judgment is essential here.',
  };
}

const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  padding: '2px 8px',
  borderRadius: 12,
};

export function ClassificationTab({
  documentId,
  rowVersion,
  classifierSuggestion,
  classifierConfidence,
  currentApprovedValue,
  currentApprovedAt,
  currentApprovedByEmail,
  sections,
  canApprove,
  onSuccess,
  layer1Proposal,
}: ClassificationTabProps): ReactElement {
  const [overrideSection, setOverrideSection] = useState<string>('');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [overrideRevision, setOverrideRevision] = useState<Document['RowVersion']>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const overrideEnabled =
    overrideSection !== '' && sectionKey(overrideSection) !== sectionKey(classifierSuggestion) && overrideReason.trim() !== '';

  const handleApproveSuggestion = async (): Promise<void> => {
    if (!classifierSuggestion || saving) return;
    setSaving(true);
    setError(null);
    try {
      await v2.documents.approveClassification(documentId, {
        approvedClassification: classifierSuggestion,
        classifierSuggestion,
        classifierConfidence,
      }, rowVersion);
      onSuccess(classifierSuggestion);
    } catch (err) {
      setError(formatApiError(err, 'Could not approve classification.', ERROR_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  const handleApproveOverride = async (): Promise<void> => {
    if (!overrideEnabled || saving) return;
    setSaving(true);
    setError(null);
    try {
      await v2.documents.approveClassification(documentId, {
        approvedClassification: overrideSection,
        reason: overrideReason.trim(),
        classifierSuggestion,
        classifierConfidence,
      }, overrideRevision);
      onSuccess(overrideSection);
      setOverrideSection('');
      setOverrideReason('');
    } catch (err) {
      setError(formatApiError(err, 'Could not approve override.', ERROR_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  // phase-compiler-screen-batch item 2 — normalisation moved to
  // `lib/confidence` so this panel and the CTD tree share one implementation.
  // The prop is already the authoritative `ClassificationConfidence`, so it is
  // passed under that key.
  const confidencePct = confidencePercent({ ClassificationConfidence: classifierConfidence });

  const band = confidencePct !== null ? confidenceBand(confidencePct) : null;
  const isConfirmed = Boolean(currentApprovedValue && currentApprovedAt);
  const confirmedMatchesSuggestion = sectionKey(currentApprovedValue) === sectionKey(classifierSuggestion);

  return (
    <div data-testid="classification-tab" style={{ padding: '8px 4px' }}>
      {classifierSuggestion || isConfirmed ? (
        <>
          {/* ── Confidence band (phase-review-surface-strengthen) ── */}
          {band && (
            <div
              data-testid="confidence-band"
              data-confidence-level={bandForPercent(confidencePct!)}
              style={{
                padding: '8px 10px',
                background: band.bg,
                border: `1px solid ${band.border}`,
                borderRadius: 5,
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                <span
                  data-testid="confidence-band-label"
                  style={{ ...pillStyle, background: band.bg, color: band.color }}
                >
                  <span aria-hidden="true">{band.icon}</span>
                  {band.label}
                </span>
                <span
                  data-testid="confidence-band-pct"
                  style={{ fontSize: 13, fontWeight: 700, color: band.color }}
                >
                  {confidencePct}%
                </span>
              </div>
              <div style={{ fontSize: 11, color: band.color, lineHeight: 1.4 }}>
                {band.description}
              </div>
            </div>
          )}

          {/* ── Agent suggestion block ── */}
          <div
            data-testid="classification-suggestion-block"
            style={{
              padding: 10,
              background: isConfirmed ? '#F0FDF4' : '#FFFBEB',
              border: isConfirmed ? '1px solid #BBF7D0' : '1px solid #FDE68A',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {/* Status label: agent-suggested vs human-confirmed */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {isConfirmed ? (
                <span
                  data-testid="classification-status-confirmed"
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                    color: '#15803D',
                    background: 'rgba(34,197,94,0.15)',
                    padding: '1px 7px',
                    borderRadius: 10,
                  }}
                >
                  ✓ HUMAN CONFIRMED
                </span>
              ) : (
                <span
                  data-testid="classification-status-suggested"
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                    color: '#92400E',
                    background: 'rgba(245,158,11,0.15)',
                    padding: '1px 7px',
                    borderRadius: 10,
                  }}
                >
                  ⚠ PENDING REVIEW
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6B7280', letterSpacing: '0.06em' }}>
              {isConfirmed ? 'Confirmed section' : 'Classifier suggested'}
            </div>
            <div data-testid="classification-suggestion-code" style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>
              {isConfirmed ? currentApprovedValue : classifierSuggestion}
            </div>
            {isConfirmed && classifierSuggestion && !confirmedMatchesSuggestion && (
              <div style={{ fontSize: 11, color: '#92400E', marginTop: 3 }}>
                ↳ Overridden from AI suggestion: <em>{classifierSuggestion}</em>
              </div>
            )}
            {/* phase-classification-stage-1-filename-layer — the Layer 1
                hint. Only shown while awaiting confirmation: once a human
                has confirmed, the proposal is moot. Never a substitute for
                the classifier's own suggestion above — advisory only. */}
            {!isConfirmed && layer1Proposal?.status === 'proposal' && (
              <div
                data-testid="classification-layer1-hint"
                style={{
                  fontSize: 11,
                  color: '#374151',
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: '1px dashed #E5E7EB',
                }}
              >
                ⚡ Layer 1 suggests:{' '}
                <strong>{layer1Proposal.family ? humanizeFamilyCode(layer1Proposal.family) : '—'}</strong>
                {layer1Proposal.section ? ` — ${layer1Proposal.section}` : ''}
                {typeof layer1Proposal.supportCount === 'number'
                  ? ` (${layer1Proposal.supportCount} confirmed match${layer1Proposal.supportCount === 1 ? '' : 'es'} for this filename pattern)`
                  : ''}
                . Not applied automatically — review before confirming.
              </div>
            )}
            {!isConfirmed && layer1Proposal?.status === 'ambiguous' && (
              <div
                data-testid="classification-layer1-hint"
                style={{
                  fontSize: 11,
                  color: '#92400E',
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: '1px dashed #E5E7EB',
                }}
              >
                ⚡ Layer 1 found conflicting patterns for this filename across more than one
                document family — no suggestion offered; review carefully.
              </div>
            )}
          </div>
        </>
      ) : (
        <div
          data-testid="classification-no-suggestion"
          style={{ fontSize: 12, color: '#6B7280', marginBottom: 12, fontStyle: 'italic' }}
        >
          No classifier suggestion yet. Approve any section to commit a manual classification.
        </div>
      )}

      {/* Approval button — only shown when not yet confirmed */}
      {classifierSuggestion && !isConfirmed && (
        <button
          type="button"
          data-testid="classification-approve-suggestion-btn"
          onClick={() => void handleApproveSuggestion()}
          disabled={saving || !canApprove}
          style={{
            padding: '8px 12px',
            fontSize: 13,
            fontWeight: 600,
            background: saving || !canApprove ? '#E5E7EB' : '#15803D',
            color: saving || !canApprove ? '#6B7280' : '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: saving || !canApprove ? 'not-allowed' : 'pointer',
            marginBottom: 12,
            width: '100%',
          }}
        >
          {saving ? 'Approving…' : 'Confirm this classification'}
        </button>
      )}

      <div
        style={{
          fontSize: 11,
          textAlign: 'center',
          color: '#9CA3AF',
          margin: '8px 0',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        — or override —
      </div>

      <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 }}>
        Section
      </label>
      <select
        data-testid="classification-override-select"
        value={overrideSection}
        onChange={(e) => {
          if (overrideSection === '') setOverrideRevision(rowVersion);
          setOverrideSection(e.target.value);
        }}
        disabled={!canApprove || saving}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: 13,
          marginBottom: 8,
          borderRadius: 4,
          border: '1px solid #D1D5DB',
        }}
      >
        <option value="">— Pick a different section —</option>
        {sections.map((s) => (
          <option key={s.code} value={s.code}>
            {s.code} — {s.label}
          </option>
        ))}
      </select>

      <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 }}>
        Reason for override (required) <span style={{ color: '#B91C1C' }}>*</span>
      </label>
      <textarea
        data-testid="classification-override-reason"
        value={overrideReason}
        onChange={(e) => setOverrideReason(e.target.value)}
        disabled={!canApprove || saving}
        rows={2}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: 13,
          marginBottom: 8,
          borderRadius: 4,
          border: '1px solid #D1D5DB',
          fontFamily: 'inherit',
        }}
      />

      <button
        type="button"
        data-testid="classification-approve-override-btn"
        onClick={() => void handleApproveOverride()}
        disabled={!overrideEnabled || saving || !canApprove}
        style={{
          padding: '8px 12px',
          fontSize: 13,
          fontWeight: 600,
          background: overrideEnabled && !saving && canApprove ? '#15803D' : '#E5E7EB',
          color: overrideEnabled && !saving && canApprove ? '#fff' : '#6B7280',
          border: 'none',
          borderRadius: 4,
          cursor: overrideEnabled && !saving && canApprove ? 'pointer' : 'not-allowed',
          width: '100%',
        }}
      >
        {saving ? 'Approving…' : 'Approve override'}
      </button>

      {error && (
        <div
          data-testid="classification-error"
          role="alert"
          style={{
            marginTop: 10,
            padding: '6px 10px',
            background: '#FEE2E2',
            color: '#B91C1C',
            border: '1px solid #B91C1C',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {/* Approval history — shown when confirmed */}
      {currentApprovedValue && currentApprovedAt && (
        <div
          data-testid="classification-history"
          style={{
            marginTop: 16,
            paddingTop: 10,
            borderTop: '1px solid #E5E7EB',
            fontSize: 12,
            color: '#6B7280',
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 4,
            }}
          >
            Approval history
          </div>
          <div>
            Confirmed {new Date(currentApprovedAt).toISOString().slice(0, 10)}
            {currentApprovedByEmail ? ` by ${currentApprovedByEmail}` : ''} to:{' '}
            <strong style={{ color: '#111827' }}>{currentApprovedValue}</strong>{' '}
            {!classifierSuggestion ? '(manual classification)' : confirmedMatchesSuggestion
              ? '(matches recorded classification)'
              : '(OVERRIDE — human judgment)'}
          </div>
        </div>
      )}
    </div>
  );
}
